import { sampleResultLoaderName } from './loaders/loader-owner'
import type { SharedExtensionMetaInfo } from './model/model-types'
import type { MethodParams } from './params'

// A declared `result`. Not a schema slot — it is a UNION zod cannot represent (a literal sample value, a zod
// schema carrying `.example()`, or a loader fn that derives the sample from the current params), so it gets
// its own wrapper rather than living in ../params.ts.
//
// It answers two questions for every owner (actions, both trigger kinds, dictionaries): what `returnType`
// the method publishes, and where its sample comes from — an inline `sampleResult`, or a generated
// SAMPLE_RESULT_LOADER method the console calls with the params filled in so far.

// The loader is called with the owner's handler bag plus the current params.
type LoaderFn = (bag: object) => unknown

function inferReturnType(value: unknown): string {
  if (Array.isArray(value)) {
    return 'LIST'
  }

  if (value && typeof value === 'object') {
    return 'OBJECT'
  }

  if (typeof value === 'string') {
    return 'STRING'
  }

  return 'VOID'
}

function isZodSchema(value: unknown): boolean {
  return !!value && typeof value === 'object' && '_zod' in (value as Record<string, unknown>)
}

// A zod `result` carries its sample in `.example(...)` → native `meta.examples`; the last one wins.
function schemaExample(schema: { meta?: () => { examples?: unknown[] } | undefined }): unknown {
  const examples = typeof schema.meta === 'function' ? schema.meta()?.examples : undefined

  return examples && examples.length ? examples[examples.length - 1] : null
}

export class ResultSpec {
  static from(result: unknown): ResultSpec {
    return new ResultSpec(result)
  }

  private constructor(private readonly declared: unknown) {}

  // The sample is not knowable statically — it comes from a generated loader method instead.
  get isLoader(): boolean {
    return typeof this.declared === 'function'
  }

  get returnType(): string {
    if (this.isLoader || isZodSchema(this.declared)) {
      return 'OBJECT'
    }

    return inferReturnType(this.declared)
  }

  // Whether anything was declared at all — a dictionary with no `result` still publishes OBJECT.
  get isDeclared(): boolean {
    return this.declared !== undefined
  }

  // The inline sample, or null when there is none (loader form, or an undeclared result).
  #sampleResult(): unknown {
    if (this.isLoader) {
      return null
    }

    if (isZodSchema(this.declared)) {
      return schemaExample(this.declared as { meta?: () => { examples?: unknown[] } | undefined })
    }

    return this.declared ?? null
  }

  // The `metaInfo` halves that describe the output. In loader form the sample is replaced by a reference to
  // the generated method, which depends on every param of its owner (any of them can change the shape).
  toMetaInfo(ownerId: string, params: MethodParams): Pick<SharedExtensionMetaInfo, 'sampleResult' | 'sampleResultLoader'> {
    if (this.isLoader) {
      return {
        sampleResult: null,
        sampleResultLoader: { methodName: sampleResultLoaderName(ownerId), dependsOn: params.keys() },
      }
    }

    return { sampleResult: this.#sampleResult(), sampleResultLoader: null }
  }

  // Run the loader form against a handler bag carrying the current params.
  async runLoader(bag: object): Promise<unknown> {
    return (this.declared as LoaderFn)(bag)
  }
}
